import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { LayoutDashboard, Truck, Users, Building2, LogOut, Bell, CalendarClock, Wallet, MapPin, MessageCircle, Star, ClipboardList, Boxes, Banknote, Car, Briefcase, Menu, Archive, Contact, ShieldCheck, FileText, Megaphone } from "lucide-react";
import { Logo } from "@/components/Logo";

const ROLE_LABELS = { admin: "Administrator", manager: "Manager", calling_executive: "Calling Executive" };

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/leads", label: user?.role === "calling_executive" ? "My Leads" : "Leads", icon: Truck, roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/ilot", label: "ILOT", icon: Boxes, roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/upcoming", label: "Packing Schedule", icon: ClipboardList, roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/customers", label: "Customers", icon: Contact, roles: ["admin", "manager"] },
    { to: "/dashboard/vendors", label: "Vendors", icon: Building2, roles: ["admin", "manager", "calling_executive"] },
    { to: "/dashboard/quotes", label: "Vendor Quotes", icon: FileText, roles: ["admin", "manager"] },
    { to: "/dashboard/vehicles", label: "Vehicles", icon: Car, roles: ["admin", "manager"] },
    { to: "/dashboard/tracking", label: "Live Tracking", icon: MapPin, roles: ["admin", "manager"] },
    { to: "/dashboard/accounts", label: "Accounts", icon: Wallet, roles: ["admin", "manager"] },
    { to: "/dashboard/vendor-payments", label: "Vendor Payments", icon: Banknote, roles: ["admin", "manager"] },
    { to: "/dashboard/insurance", label: "Insurance", icon: ShieldCheck, roles: ["admin", "manager"] },
    { to: "/dashboard/feedback", label: "Feedback", icon: Star, roles: ["admin", "manager"] },
    { heading: "Marketing", to: "#marketing", roles: ["admin", "manager"] },
    { to: "/dashboard/meta-leads", label: "Meta Leads", icon: Megaphone, roles: ["admin", "manager"] },
    { heading: "Admin", to: "#admin", roles: ["admin", "manager"] },
    { to: "/dashboard/office", label: "Office", icon: Briefcase, roles: ["admin", "manager"] },
    { to: "/dashboard/history", label: "Deleted Leads", icon: Archive, roles: ["admin", "manager"] },
    { to: "/dashboard/users", label: "Users", icon: Users, roles: ["admin", "manager"] },
  ].filter((i) => i.roles.includes(user?.role));

  const bottomTabs = [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/dashboard/leads", label: "Leads", icon: Truck },
    { to: "/dashboard/ilot", label: "ILOT", icon: Boxes },
    { to: "/dashboard/upcoming", label: "Packing", icon: ClipboardList },
  ];

  const sidebar = (onNavigate) => (
    <>
      <div className="h-16 flex items-center px-4 border-b border-border shrink-0">
        <Logo className="h-8" />
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((it) => it.heading ? (
          <div key={it.to} className="px-5 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">{it.heading}</div>
        ) : (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                isActive
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
              }`
            }
          >
            <it.icon className="h-4 w-4" />
            <span className="flex-1">{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4 shrink-0">
        <div className="text-sm font-bold truncate">{user?.name}</div>
        <div className="text-[11px] uppercase tracking-wide text-primary font-bold mb-3">{ROLE_LABELS[user?.role]}</div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="w-full flex items-center justify-center gap-2 border border-border py-2 text-sm hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-border bg-card flex-col sticky top-0 h-screen">
        {sidebar()}
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-card flex flex-col gap-0" data-testid="mobile-nav">
          {sidebar(() => setMobileOpen(false))}
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border bg-card flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden h-9 w-9 grid place-items-center border border-border shrink-0 hover:bg-muted transition-colors"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="font-heading font-bold text-base sm:text-lg tracking-tight uppercase truncate">Logistics Console</div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline text-sm text-muted-foreground hover:text-primary transition-colors">Public Form ↗</a>
            <RemindersBell />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6 overflow-x-auto">
          <Outlet />
        </main>
        {/* Mobile bottom tab bar */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-card grid grid-cols-4" data-testid="mobile-bottom-nav">
          {bottomTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`bottomnav-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`
              }
            >
              <t.icon className="h-5 w-5" />
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function RemindersBell() {
  const nav = useNavigate();
  const [due, setDue] = useState([]);
  const [seen, setSeen] = useState(0);

  useEffect(() => {
    const load = () =>
      api.get("/leads/reminders").then((r) => {
        setDue(r.data);
        setSeen((prev) => {
          if (r.data.length > prev) {
            const latest = r.data[r.data.length - 1];
            import("sonner").then(({ toast }) => toast.warning(`Follow-up due: ${latest.order_number} · ${latest.customer_name}`));
          }
          return r.data.length;
        });
      }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 grid place-items-center border border-border bg-card hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="reminders-bell" aria-label="Follow-up reminders">
          <Bell className="h-4 w-4" />
          {due.length > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center" data-testid="reminders-count">{due.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-none p-0" data-testid="reminders-panel">
        <div className="px-4 py-3 border-b border-border font-heading font-bold uppercase tracking-tight text-sm flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Due Follow-ups</div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {due.length === 0 && <div className="p-4 text-sm text-muted-foreground">No follow-ups due right now.</div>}
          {due.map((d) => (
            <button key={d.id} onClick={() => nav("/dashboard/leads")} className="w-full text-left p-3 hover:bg-muted transition-colors" data-testid={`reminder-${d.order_number}`}>
              <div className="flex items-center justify-between"><span className="font-mono-num text-primary text-sm">{d.order_number}</span><span className="text-[11px] text-muted-foreground">{new Date(d.followup_datetime).toLocaleString()}</span></div>
              <div className="text-sm font-medium">{d.customer_name} · <span className="font-mono-num text-xs text-muted-foreground">{d.customer_phone}</span></div>
              {d.remarks && <div className="text-xs text-muted-foreground truncate">{d.remarks}</div>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
